import { Link, navigate } from "gatsby"
import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { Event, track } from "../../analytics"
import { TALKS_PLAYLIST_URL, RESUME_URL } from "constants/links"
import { PageWidth } from "components/common/Section"

const HeaderContainer = styled.header`
  position: sticky;
  top: 16px;
  z-index: 20;
  width: min(760px, calc(100% - 32px));
  margin: 20px auto 0;
  height: 60px;
  @media (max-width: 700px) { height: 64px; }
`
const Glass = styled.div`
  position: relative;
  width: ${p => p.$compact ? "calc(100% - 32px)" : "100%"};
  margin-inline: auto;
  transform: translateY(${p => p.$compact ? "-4px" : "0"});
  transition: width 650ms cubic-bezier(.22,1,.36,1), transform 650ms cubic-bezier(.22,1,.36,1), background-color 300ms ease, border-color 300ms ease;
  @media (max-width: 700px) {
    width: ${p => p.$compact ? "calc(100% - 16px)" : "100%"};
    transform: translateY(${p => p.$compact ? "-6px" : "0"});
  }
  @media (max-width: 360px) {
    width: ${p => p.$compact ? "calc(100% - 8px)" : "100%"};
  }
  border: 1px solid ${p => p.theme.border};
  border-radius: 100px;
  background: ${p => p.theme.colors.background.primary[500]}99;
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  backdrop-filter: blur(8px) saturate(150%);
  box-shadow: 0 8px 32px #00000008, inset 0 1px 0 #ffffff55;
  @supports not (backdrop-filter: blur(8px)) {
    background: ${p => p.theme.colors.background.primary[500]}ed;
  }
  @media (prefers-reduced-transparency: reduce) {
    background: ${p => p.theme.colors.background.primary[500]};
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
`
const Bar = styled(PageWidth)`
  width: 100%;
  min-height: ${p => p.$compact ? "52px" : "58px"};
  padding: ${p => p.$compact ? "3px 10px 3px 24px" : "6px 10px 6px 24px"};
  transition: min-height 650ms cubic-bezier(.22,1,.36,1), padding 650ms cubic-bezier(.22,1,.36,1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  @media (max-width: 700px) {
    width: 100%;
    min-height: ${p => p.$compact ? "52px" : "62px"};
    gap: 8px;
    padding: ${p => p.$compact ? "3px 8px 3px 10px" : "8px 10px 8px 14px"};
  }
  @media (max-width: 360px) {
    padding: ${p => p.$compact ? "3px 6px 3px 10px" : "8px 10px 8px 14px"};
  }
`
const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.025em;
  span:last-child {
    line-height: 1.25;
  }
  @media (max-width: 360px) { display: none; }
  @media (max-width: 620px) {
    span:last-child {
      display: none;
    }
  }
`
const Monogram = styled.span`
  width: 38px;
  height: 38px;
  display: none;
  place-items: center;
  border: 1px solid ${p => p.theme.border};
  border-radius: 50%;
  color: ${p => p.theme.colors.accent.primary[500]};
  font-size: 14px;
  letter-spacing: -0.07em;
  @media (max-width: 620px) { display: grid; }
`
const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 540px) {
    gap: 4px;
  }
`
const NavLink = styled(Link)`
  white-space: nowrap;
  min-height: 44px;
  font-size: 13px;
  text-decoration: none;
  color: ${p => p.theme.muted};
  padding: 10px 18px;
  border-radius: 100px;
  transition: background 240ms ease, color 240ms ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  &:hover,
  &[aria-current="page"] {
    color: ${p => p.theme.colors.text.primary[500]};
    background: ${p => p.theme.surface};
  }
  @media (max-width: 540px) { padding: 10px 9px; font-size: 12px; }
`
const ToggleButton = styled.button`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${p => p.theme.border};
  background: transparent;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  transition: background-color 300ms ease, border-color 300ms ease, transform 250ms ease;
  &:hover { background: ${p => p.theme.surface}; }
  &:active { transform: scale(.94); }
  svg { position: absolute; transition: opacity 350ms ease, transform 550ms cubic-bezier(.22,1,.36,1); }
  .sun { opacity: ${p => p.$dark ? 1 : 0}; transform: rotate(${p => p.$dark ? "0deg" : "-90deg"}) scale(${p => p.$dark ? 1 : .5}); }
  .moon { opacity: ${p => p.$dark ? 0 : 1}; transform: rotate(${p => p.$dark ? "90deg" : "0deg"}) scale(${p => p.$dark ? .5 : 1}); }

`
const BackButton = styled.button`
  border: 0;
  padding: 10px 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: ${p => p.theme.muted};
`
const SkipLink = styled.a`
  position: fixed;
  top: 12px;
  left: 20px;
  clip-path: inset(50%);
  opacity: 0;
  pointer-events: none;
  padding: 10px 16px;
  background: ${p => p.theme.colors.accent.primary[500]};
  color: ${p => p.theme.accentInk};
  z-index: 100;
  &:focus {
    clip-path: none;
    opacity: 1;
    pointer-events: auto;
  }
`
const Header = ({ toggleTheme, currentTheme, showBackButton, backTo }) => {
  const [compact, setCompact] = useState(false)
  useEffect(() => {
    let frame = null
    let isCompact = false
    const update = () => {
      frame = null
      const scroll = Math.max(0, window.scrollY)
      // Separate thresholds keep the state steady near the top of the page.
      const next = isCompact ? scroll > 48 : scroll > 96
      if (next !== isCompact) { isCompact = next; setCompact(next) }
    }
    const onScroll = () => { if (frame === null) frame = window.requestAnimationFrame(update) }
    update()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [])
  return (
  <HeaderContainer>
    <SkipLink href="#main-content">Skip to content</SkipLink>
    <Glass $compact={compact}>
    <Bar $compact={compact}>
      <Brand to="/" aria-label="Chaitanya Deorukhkar — Home">
        <Monogram aria-hidden="true">cd.</Monogram>
        <span>
          Chaitanya Deorukhkar
        </span>
      </Brand>
      <Navigation aria-label="Main navigation">
        {showBackButton ? (
          <BackButton
            onClick={() => {
              track(Event.back_clicked, { to: backTo || "unknown" })
              backTo ? navigate(`/${backTo}`) : window.history.back()
            }}
          >
            ←{" "}
            {backTo ? backTo.charAt(0).toUpperCase() + backTo.slice(1) : "Back"}
          </BackButton>
        ) : (
          <>
            <NavLink
              to="/"
              onClick={() =>
                track(Event.navigation_tab_clicked, { tab: "Home" })
              }
            >
              Home
            </NavLink>
            <NavLink
              as="a"
              href={TALKS_PLAYLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Talks (opens YouTube playlist in a new tab)"
              onClick={() =>
                track(Event.navigation_tab_clicked, { tab: "Talks" })
              }
            >
              Talks <span aria-hidden="true">↗</span>
            </NavLink>
            <NavLink
              as="a"
              href={RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Resume (opens Google Drive folder in a new tab)"
              onClick={() => track(Event.navigation_tab_clicked, { tab: "Resume" })}
            >
              Resume <span aria-hidden="true">↗</span>
            </NavLink>
          </>
        )}
        <ToggleButton
          $dark={currentTheme === "dark"}
          type="button"
          onClick={toggleTheme}
          aria-label={`Switch to ${
            currentTheme === "dark" ? "light" : "dark"
          } theme`}
          title={`Switch to ${
            currentTheme === "dark" ? "light" : "dark"
          } theme`}
        >
          <svg className="sun" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.4 1.4m11.2 11.2L19 19M5 19l1.4-1.4M17.6 6.4 19 5" />
          </svg>
          <svg className="moon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
            <path d="M20.5 13A8.6 8.6 0 0 1 11 3.5 8.6 8.6 0 1 0 20.5 13Z" />
          </svg>
        </ToggleButton>
      </Navigation>
    </Bar>
    </Glass>
  </HeaderContainer>
)
}
export default Header


